import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationService } from '@/lib/services/notification.service';
import { useDragState } from '../useDragState';
import { useFileValidation } from '../useFileValidation';
import { logger } from '@/lib/utils/logger';

interface UseDragEventsProps {
  onFilesDropped: (files: File[]) => Promise<void>;
  agentId?: string;
}

export const useDragEvents = ({ onFilesDropped, agentId }: UseDragEventsProps) => {
  const { t } = useTranslation('chats');
  const { isDragOver, incrementDragCounter, decrementDragCounter, resetDragState } = useDragState();
  const { getPartitionedFiles, hasAcceptedFiles, getValidFiles, notifyRejectedFiles } =
    useFileValidation(agentId);

  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!e.dataTransfer?.types.includes('Files')) return;

      // Only increment counter if there are accepted file types
      if (hasAcceptedFiles(e.dataTransfer)) {
        incrementDragCounter();
      }
    },
    [hasAcceptedFiles, incrementDragCounter],
  );

  const handleDragLeave = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only decrement if we're actually tracking file drags
      if (!e.dataTransfer?.types.includes('Files')) return;

      decrementDragCounter();
    },
    [decrementDragCounter],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set dropEffect for file drags
    if (e.dataTransfer) {
      if (e.dataTransfer.types.includes('Files')) {
        e.dataTransfer.dropEffect = 'copy';
      } else {
        e.dataTransfer.dropEffect = 'none';
      }
    }
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Early return for non-file drops
      if (!e.dataTransfer?.types.includes('Files')) return;

      resetDragState();

      const [acceptedFiles, rejectedFiles] = getPartitionedFiles(e.dataTransfer);

      notifyRejectedFiles(rejectedFiles);

      // Process accepted files
      if (acceptedFiles.length > 0) {
        try {
          const validFiles = getValidFiles(acceptedFiles);
          await onFilesDropped(validFiles);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          notificationService.showError(
            t('chats.useDragEvents.errorHandlingDroppedFiles', { error: errorMessage }),
          );
        }
      }
    },
    [resetDragState, getPartitionedFiles, getValidFiles, notifyRejectedFiles, onFilesDropped, t],
  );

  // Set up document event listeners
  useEffect(() => {
    const handleDropWithErrorHandling = (e: DragEvent) => {
      handleDrop(e).catch((error: unknown) => {
        logger.error('Unexpected error in drop handler:', error);
        notificationService.showError(t('chats.useDragEvents.anUnexpectedErrorOccurredWhile'));
      });
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDropWithErrorHandling);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDropWithErrorHandling);
      resetDragState();
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop, resetDragState, t]);

  return { isDragOver };
};
